import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useDbProductImagesByProductIds, useDbCategories, toCartProduct, useVariantStockSummary } from "@/hooks/use-products";
import ProductCard from "@/components/products/ProductCard";
import type { DbProduct } from "@/types/product";
import { Trophy } from "lucide-react";

const PopularRacketsSection = () => {
  // Fetch popular racket entries
  const { data: entries = [], isLoading: loadingEntries } = useQuery({
    queryKey: ["popular-rackets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("popular_rackets")
        .select("product_id, sort_order")
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

  const productIds = useMemo(() => entries.map((e) => e.product_id), [entries]);

  // Fetch product data
  const { data: rawProducts = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["popular-racket-products", productIds],
    queryFn: async () => {
      if (productIds.length === 0) return [];
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .in("id", productIds)
        .eq("status", "active");
      if (error) throw error;
      return (data ?? []) as unknown as DbProduct[];
    },
    enabled: productIds.length > 0,
    staleTime: 60_000,
  });

  const { data: images = [] } = useDbProductImagesByProductIds(productIds);
  const { data: categories = [] } = useDbCategories();
  const { data: variantStockMap } = useVariantStockSummary();

  // Build products in the correct sort order
  const products = useMemo(() => {
    const productMap = new Map(rawProducts.map((p) => [p.id, p]));
    return entries
      .map((e) => {
        const p = productMap.get(e.product_id);
        if (!p) return null;
        const imgs = images.filter((i) => i.product_id === p.id);
        return toCartProduct(p, imgs, categories, variantStockMap);
      })
      .filter(Boolean) as ReturnType<typeof toCartProduct>[];
  }, [entries, rawProducts, images, categories, variantStockMap]);

  if (loadingEntries || loadingProducts) {
    return (
      <section className="container mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Trophy size={28} className="text-primary" />
          <h2 className="font-display text-2xl md:text-3xl font-bold">Top 5 Populārākās Raketes</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-muted rounded-xl h-[370px] animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-12 md:py-16">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Trophy size={22} className="text-primary" />
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold">Top {products.length} Populārākās Raketes</h2>
          <p className="text-sm text-muted-foreground">Šī mēneša visvairāk pieprasītās raketes</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
        {products.map((product, idx) => (
          <div key={product.id} className="relative">
            {/* Popularity rank badge */}
            <div className="absolute -top-2 -left-2 z-30 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-background">
              #{idx + 1}
            </div>
            <ProductCard product={product} hideTopBadges priority={idx < 5} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default PopularRacketsSection;
